import { InputMaybe, PreferredHotelProviderType, PriceComparisonPayload, PriceComparisonType, DeviceType } from '@graphql/generated';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { useMemo } from 'react';
import { useApiMetadata } from './useApiMetadata';
import { useClientReference } from './useClientReference';

const isValidPriceComparisonType = (value?: InputMaybe<PriceComparisonType>): value is PriceComparisonType =>
  Object.values(PriceComparisonType).includes(value as PriceComparisonType);

const isValidPreferredHotelProviderType = (value?: InputMaybe<PreferredHotelProviderType>): value is PreferredHotelProviderType =>
  Object.values(PreferredHotelProviderType).includes(value as PreferredHotelProviderType);

const isValidMarginPercentage = (value?: InputMaybe<number>): value is number => typeof value === 'number' && !isNaN(value) && value >= 0 && value <= 100;

const isValidPriceComparePayload = (payload: PriceComparisonPayload | undefined) => {
  if (!payload) return false;
  return (
    isValidPriceComparisonType(payload.comparisonType) &&
    isValidPreferredHotelProviderType(payload.preferredProvider) &&
    isValidMarginPercentage(payload.preferredProviderPriceMaxExtraPercent)
  );
};

/**
 * Intended to be used to generate the metadata argument in the
 * `travelPlacesPricingAndBookingDetails` and `searchExtendedAccommodationProperties`
 * queries.
 */
export const useSearchAccommodationsQueryMetadataPayload = (): {
  isPreparingSearchMetadataPayload: boolean;
  searchMetadata: {
    eventId: string | undefined;
    anonymousId: string | undefined;
    deviceType: DeviceType | undefined;
    userCountryCode: string | undefined;
    preferredProviderType: PreferredHotelProviderType | undefined;
    priceComparisonPayload: PriceComparisonPayload | undefined;
  } | null;
} => {
  const { deviceType, anonymousId, eventId, userCountryCode } = useApiMetadata();
  const { loadingClientReference } = useClientReference();

  const {
    value: accommodationsGuestPriceCompareType,
    loading: accommodationsGuestPriceCompareTypeLoading,
    payload: accommodationsGuestPriceCompareTypeLoadingPayload,
  } = useRemoteFeatureFlag('accommodationsGuestPriceCompareType');

  const preferredProviderType = accommodationsGuestPriceCompareType as PreferredHotelProviderType;
  const priceComparisonPayload = useMemo(() => {
    const remotePayload = (Object(accommodationsGuestPriceCompareTypeLoadingPayload) as PriceComparisonPayload) || {};
    return {
      comparisonType: remotePayload.comparisonType,
      preferredProvider: remotePayload.preferredProvider,
      preferredProviderPriceMaxExtraPercent: remotePayload.preferredProviderPriceMaxExtraPercent,
    };
  }, [accommodationsGuestPriceCompareTypeLoadingPayload]);

  const shouldUsePriceComparison = accommodationsGuestPriceCompareType === 'pricecomparison';
  let validPriceComparisonPayload: PriceComparisonPayload | undefined;
  if (shouldUsePriceComparison && isValidPriceComparePayload(priceComparisonPayload)) {
    validPriceComparisonPayload = priceComparisonPayload;
  }
  // Validate preferredProviderType values come from amplitude
  let validPreferredProviderType: PreferredHotelProviderType | undefined;
  if ((!shouldUsePriceComparison || !validPriceComparisonPayload) && isValidPreferredHotelProviderType(preferredProviderType)) {
    validPreferredProviderType = preferredProviderType;
  }

  const isPreparingSearchMetadataPayload = loadingClientReference || accommodationsGuestPriceCompareTypeLoading;

  return useMemo(() => {
    return {
      isPreparingSearchMetadataPayload,
      searchMetadata: isPreparingSearchMetadataPayload
        ? null
        : {
            eventId,
            deviceType,
            anonymousId,
            userCountryCode,
            priceComparisonPayload: validPriceComparisonPayload,
            preferredProviderType: validPreferredProviderType,
          },
    };
  }, [isPreparingSearchMetadataPayload, eventId, validPriceComparisonPayload, validPreferredProviderType, anonymousId, deviceType, userCountryCode]);
};
